import { flushAnalyticsNow } from '@/hooks/client/use-analytics-engine'
import type { HubVisit } from '@/lib/analytics/hub-visit'

const measureScroll = (visit: HubVisit) => {
  const height = document.documentElement.scrollHeight - window.innerHeight
  visit.scrolled(height > 0 ? window.scrollY / height : 0)
}

const listenForSnapshots = (visit: HubVisit) => {
  const snapshot = (reason: 'hidden' | 'pagehide') => {
    measureScroll(visit)
    visit.snapshot(reason)
    flushAnalyticsNow()
  }
  const hidden = () => {
    if (document.visibilityState === 'hidden') {
      snapshot('hidden')
    }
  }
  const pagehide = () => snapshot('pagehide')
  document.addEventListener('visibilitychange', hidden)
  window.addEventListener('pagehide', pagehide)
  return () => {
    document.removeEventListener('visibilitychange', hidden)
    window.removeEventListener('pagehide', pagehide)
  }
}

const listenForInteractions = (root: HTMLElement, visit: HubVisit) => {
  const clicked = (event: MouseEvent) => {
    if ((event.type !== 'auxclick' || event.button === 1) && event.target instanceof Element) {
      if (event.target.closest('a[href], button, input, select')) {
        visit.interacted()
      }
    }
  }
  root.addEventListener('click', clicked)
  root.addEventListener('auxclick', clicked)
  return () => {
    root.removeEventListener('click', clicked)
    root.removeEventListener('auxclick', clicked)
  }
}

export const observeHubVisit = (root: HTMLElement, visit: HubVisit) => {
  let active = true
  // AnalyticsProvider's effect runs after child effects.
  queueMicrotask(() => {
    if (active) {
      visit.start()
      measureScroll(visit)
    }
  })
  const stopSnapshots = listenForSnapshots(visit)
  const stopInteractions = listenForInteractions(root, visit)
  const interval = window.setInterval(visit.checkEngagement, 1_000)
  const scroll = () => measureScroll(visit)
  window.addEventListener('scroll', scroll, { passive: true })
  return () => {
    active = false
    visit.end()
    window.clearInterval(interval)
    stopSnapshots()
    stopInteractions()
    window.removeEventListener('scroll', scroll)
  }
}
